import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import SydTextArea from '../../../../../../../../commonDesign/SydTextArea'
import SydButton from '../../../../../../../../commonDesign/Button'
import SeriesListBuilder from './SeriesListBuilder'

const xAxisTypeOptions = [
  { label: 'Category', value: 'category' },
  { label: 'Value', value: 'value' },
  { label: 'Time', value: 'time' },
  { label: 'Log', value: 'log' }
]

const xAxisPositionOptions = [
  { label: 'Bottom', value: 'bottom' },
  { label: 'Top', value: 'top' }
]

const yAxisTypeOptions = [
  { label: 'Value', value: 'value' },
  { label: 'Log', value: 'log' }
]

const yAxisPositionOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' }
]

const tooltipTriggerOptions = [
  { label: 'Axis', value: 'axis' },
  { label: 'Item', value: 'item' }
]

const EMPTY_OBJ = {}
const EMPTY_ARR = []

function valueFormatMapToText (map) {
  if (map == null || typeof map !== 'object' || Array.isArray(map)) return ''
  return JSON.stringify(map)
}
function parseValueFormatMapText (text) {
  const trimmed = text.trim()
  if (!trimmed) return null
  const parsed = JSON.parse(trimmed)
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new SyntaxError('valueFormatMap must be a JSON object')
  }
  return parsed
}

function AxisChartEditor ({ config, columns, allPurposes, onChange }) {
  const xAxis = useMemo(() => config.xAxis || EMPTY_OBJ, [config.xAxis])
  const yAxes = useMemo(() => config.yAxes || EMPTY_ARR, [config.yAxes])
  const tooltip = useMemo(() => config.tooltip || EMPTY_OBJ, [config.tooltip])
  const series = useMemo(() => config.series || EMPTY_ARR, [config.series])

  const [valueFormatMapDraft, setValueFormatMapDraft] = useState(() =>
    valueFormatMapToText(tooltip.valueFormatMap)
  )
  const [valueFormatMapError, setValueFormatMapError] = useState(null)
  useEffect(() => {
    setValueFormatMapDraft(valueFormatMapToText(tooltip.valueFormatMap))
    setValueFormatMapError(null)
  }, [tooltip.valueFormatMap])

  const columnOptions = useMemo(() => {
    return [
      { label: 'Select a column...', value: '__placeholder__' },
      ...columns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns])

  // --- X-Axis handlers ---
  const handleXAxisChange = useCallback((field, value) => {
    onChange({ xAxis: { ...xAxis, [field]: (value !== '' && value != null) ? value : null } })
  }, [onChange, xAxis])

  // --- Y-Axes handlers ---
  const handleYAxisChange = useCallback((idx, field, value) => {
    const updated = [...yAxes]
    updated[idx] = { ...updated[idx], [field]: value }
    onChange({ yAxes: updated })
  }, [onChange, yAxes])

  const handleAddYAxis = useCallback(() => {
    if (yAxes.length >= 4) return
    const id = yAxes.length === 0 ? 'left' : `y${yAxes.length}`
    const position = yAxes.some(y => y.position === 'right') ? 'left' : 'right'
    onChange({
      yAxes: [...yAxes, { axisId: id, type: 'value', format: null, name: null, position, padFactor: 0 }]
    })
  }, [onChange, yAxes])

  const handleRemoveYAxis = useCallback((idx) => {
    if (yAxes.length <= 1) return
    onChange({ yAxes: yAxes.filter((_, i) => i !== idx) })
  }, [onChange, yAxes])

  // --- Tooltip handlers ---
  const handleTooltipChange = useCallback((field, value) => {
    onChange({ tooltip: { ...tooltip, [field]: value || null } })
  }, [onChange, tooltip])

  // --- Series handler ---
  const handleSeriesChange = useCallback((updatedSeries) => {
    onChange({ series: updatedSeries })
  }, [onChange])

  return (
    <div>
      {/* X-Axis */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>X-Axis</h4>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <SydLabel label='Type'>
              <SydSelect
                minWidth='0'
                options={xAxisTypeOptions}
                value={xAxis.type || 'category'}
                onChange={(e) => handleXAxisChange('type', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={3}>
            <SydLabel label='Column (accessor)'>
              <SydSelect
                minWidth='0'
                options={columnOptions}
                value={xAxis.accessor || '__placeholder__'}
                onChange={(e) => handleXAxisChange('accessor', e.target.value === '__placeholder__' ? null : e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Format' helperText='number, marketValue, percentage, date, title'>
              <SydInput
                minWidth='0'
                value={xAxis.format || ''}
                onChange={(e) => handleXAxisChange('format', e.target.value)}
                placeholder='date'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Position'>
              <SydSelect
                minWidth='0'
                options={xAxisPositionOptions}
                value={xAxis.position || 'bottom'}
                onChange={(e) => handleXAxisChange('position', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Name'>
              <SydInput
                minWidth='0'
                value={xAxis.name || ''}
                onChange={(e) => handleXAxisChange('name', e.target.value)}
                placeholder='Optional axis name'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Label Rotate' helperText='-90 to 90'>
              <SydInput
                minWidth='0'
                type='number'
                value={xAxis.labelRotate ?? ''}
                onChange={(e) => handleXAxisChange('labelRotate', e.target.value === '' ? null : Number(e.target.value))}
                placeholder='0'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={2}>
            <SydLabel label='Label Interval' helperText='0 = all, auto = fit'>
              <SydInput
                minWidth='0'
                value={xAxis.labelInterval ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  handleXAxisChange('labelInterval', v === '' ? null : (v === 'auto' ? 'auto' : Number(v)))
                }}
                placeholder='auto'
              />
            </SydLabel>
          </Grid>
        </Grid>
      </div>

      {/* Y-Axes */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>Y-Axes</h4>
          <SydButton
            size='sm'
            variant='ghost'
            onClick={handleAddYAxis}
            disabled={yAxes.length >= 4}
          >
            + Add Y-Axis
          </SydButton>
        </div>
        {yAxes.map((yAxis, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius-sm)',
              padding: '12px',
              marginBottom: '8px'
            }}
          >
            <Grid container spacing={2} alignItems='flex-end'>
              <Grid item xs={6} md={2}>
                <SydLabel label='Axis ID'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.axisId || ''}
                    onChange={(e) => handleYAxisChange(idx, 'axisId', e.target.value)}
                    placeholder='left'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Type'>
                  <SydSelect
                    minWidth='0'
                    options={yAxisTypeOptions}
                    value={yAxis.type || 'value'}
                    onChange={(e) => handleYAxisChange(idx, 'type', e.target.value)}
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Position'>
                  <SydSelect
                    minWidth='0'
                    options={yAxisPositionOptions}
                    value={yAxis.position || 'left'}
                    onChange={(e) => handleYAxisChange(idx, 'position', e.target.value)}
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Format'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.format || ''}
                    onChange={(e) => handleYAxisChange(idx, 'format', e.target.value)}
                    placeholder='marketValue'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={2}>
                <SydLabel label='Name'>
                  <SydInput
                    minWidth='0'
                    value={yAxis.name || ''}
                    onChange={(e) => handleYAxisChange(idx, 'name', e.target.value)}
                    placeholder='Optional'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={1}>
                <SydLabel label='Rotate'>
                  <SydInput
                    minWidth='0'
                    type='number'
                    value={yAxis.labelRotate ?? ''}
                    onChange={(e) => handleYAxisChange(idx, 'labelRotate', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder='0'
                  />
                </SydLabel>
              </Grid>
              <Grid item xs={6} md={1}>
                {yAxes.length > 1 && (
                  <SydButton
                    size='sm'
                    variant='ghost'
                    onClick={() => handleRemoveYAxis(idx)}
                    style={{ color: 'var(--color-danger-500)' }}
                  >
                    Remove
                  </SydButton>
                )}
              </Grid>
            </Grid>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 12px 0' }}>Tooltip</h4>
        <Grid container spacing={2}>
          <Grid item xs={6} md={6}>
            <SydLabel label='Trigger'>
              <SydSelect
                minWidth='0'
                options={tooltipTriggerOptions}
                value={tooltip.trigger || 'axis'}
                onChange={(e) => handleTooltipChange('trigger', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={6}>
            <SydLabel label='Value Format'>
              <SydInput
                minWidth='0'
                value={tooltip.valueFormat || ''}
                onChange={(e) => handleTooltipChange('valueFormat', e.target.value)}
                placeholder='marketValue'
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={6}>
            <SydLabel label='Formatter'>
              <SydInput
                minWidth='0'
                value={tooltip.formatter || ''}
                onChange={(e) => handleTooltipChange('formatter', e.target.value)}
              />
            </SydLabel>
          </Grid>
          <Grid item xs={6} md={6}>
            <SydLabel
              label='Value format map (JSON object)'
              helperText='Keys match tooltip tokens, e.g. value[1]. Values are Summit format names, e.g. marketValue.'
            >
              <SydTextArea
                minWidth='0'
                value={valueFormatMapDraft}
                onChange={(e) => {
                  setValueFormatMapError(null)
                  setValueFormatMapDraft(e.target.value)
                }}
                onBlur={() => {
                  setValueFormatMapError(null)
                  try {
                    const next = parseValueFormatMapText(valueFormatMapDraft)
                    handleTooltipChange('valueFormatMap', next)
                  } catch (err) {
                    setValueFormatMapError(
                      err instanceof Error ? err.message : 'Invalid JSON object'
                    )
                    setValueFormatMapDraft(valueFormatMapToText(tooltip.valueFormatMap))
                  }
                }}
                placeholder='{"value[1]":"marketValue","value[2]":"number"}'
              />
            </SydLabel>
            {valueFormatMapError && (
              <div style={{ color: 'var(--color-danger-500)', marginTop: 4 }}>
                {valueFormatMapError}
              </div>
            )}
          </Grid>
        </Grid>
      </div>

      {/* Series */}
      <SeriesListBuilder
        series={series}
        columns={columns}
        yAxes={yAxes}
        allPurposes={allPurposes}
        onChange={handleSeriesChange}
      />
    </div>
  )
}

AxisChartEditor.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

export default AxisChartEditor
